import ignore from 'ignore';
import { isDatabaseMigrationFile } from './supabase';
import { isCurrentChatUsingClaudeCode } from '~/lib/ai';
import type { FileMap } from '~/lib/stores/files';
import { supabaseStore } from '~/lib/stores/supabase';
import type { AppFiles } from '~/types/files';
import { removeTrailingSlashes } from '~/utils/path';
import { addWorkDir, removeWorkDir } from '~/utils/workDir';

interface IgnoreFileProps {
  path: string;
  contents: string;
  isBinary?: boolean;
}

export const BOLT_FOLDER_IGNORE = '.bolt/ignore';
export const BOLT_IGNORE = '.boltignore';
export const GIT_IGNORE = '.gitignore';

const lockFiles = new Set([
  'package-lock.json',
  'yarn.lock',
  'pnpm-lock.yaml',
  'pnpm-workspace.yaml',
  'npm-shrinkwrap.json',
]);

const faviconRegex = /(\/|^)favicon\.(\w+?)$/;
const ignoreFileRegex = /(\/|^)\.(git|bolt)(\/?)ignore$/;
const dotBoltRegex = /(\/|^)\.bolt(\/|$)/;

export function shouldIgnoreFile({ path, contents, isBinary }: IgnoreFileProps) {
  const isLockFile = lockFiles.has(removeTrailingSlashes(path).split('/').pop() as string);
  const isSVGFile = path.endsWith('.svg');
  const isFavicon = faviconRegex.test(path);
  const isIgnoreFile = ignoreFileRegex.test(path);
  const isDotBolt = dotBoltRegex.test(path);
  const isBigFile = (contents.length ?? 0) > 50_000;

  const isMigrationFileWithSupabaseConfigured = isDatabaseMigrationFile(path) && supabaseStore.isConfigured.get();

  if (isCurrentChatUsingClaudeCode()) {
    return isBinary || isDotBolt;
  }

  return (
    isBinary ||
    isLockFile ||
    isBigFile ||
    isSVGFile ||
    isFavicon ||
    isIgnoreFile ||
    isDotBolt ||
    isMigrationFileWithSupabaseConfigured
  );
}

export function createIgnoreCheckForFileMap(files: FileMap) {
  return createIgnoreCheckForFile((file) => {
    // paths in FileMap are absolute
    const filePath = addWorkDir(file);

    return files[filePath]?.type === 'file' && files[filePath].content;
  });
}

export function createIgnoreCheckForAppFiles(appFiles: AppFiles) {
  return createIgnoreCheckForFile((file) => {
    return appFiles[file]?.type === 'file' && appFiles[file].contents;
  });
}

export function createIgnoreCheck(boltIgnore = '', gitIgnore = '') {
  const ignorer = ignore();

  for (const contents of [gitIgnore, boltIgnore]) {
    contents
      .split('\n')
      .reverse()
      .forEach((entry) => {
        if (entry.trim()) {
          ignorer.add(entry);
        }
      });
  }

  return (props: IgnoreFileProps) => {
    const relativePath = removeWorkDir(props.path);

    if (relativePath.startsWith('/')) {
      return false;
    }

    return shouldIgnoreFile(props) || ignorer.test(relativePath).ignored;
  };
}

export function createIgnoreCheckForFile(getFile: (name: string) => string | false) {
  const boltIgnored = getFile(BOLT_IGNORE);
  const boltFolderIgnored = getFile(BOLT_FOLDER_IGNORE);
  const boltIgnore = boltIgnored || boltFolderIgnored || '';

  const gitIgnore = getFile(GIT_IGNORE) || '';

  return createIgnoreCheck(boltIgnore, gitIgnore);
}

export function createGitIgnoreCheckForFiles(files: FileMap) {
  const ignorer = ignore();

  function addToIgnore(contents: string) {
    contents
      .split('\n')
      .reverse()
      .forEach((entry) => {
        if (entry.trim()) {
          ignorer.add(entry);
        }
      });
  }

  const gitIgnore = files[addWorkDir(GIT_IGNORE)];

  // TODO: this logic is incomplete as it doesn't support the logic for nested .gitignore files, to be added later
  if (gitIgnore && gitIgnore.type === 'file') {
    addToIgnore(gitIgnore.content);
  }

  return (path: string) => {
    const relativePath = removeWorkDir(path);

    return ignorer.test(relativePath).ignored;
  };
}
